import { Link } from "@inertiajs/react"
import {
  Award,
  BarChart3,
  Bookmark,
  Gift,
  GraduationCap,
  Image,
  LayoutGrid,
  MailPlus,
  Megaphone,
  MessageCircle,
  Settings,
  Trophy,
  Truck,
  UserSearch,
  UsersRound,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { NavMain } from "@/components/nav-main"
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { useCurrentContext } from "@/hooks/use-current-context"
import { usePendingShipmentsCount } from "@/hooks/use-pending-shipments-count"
import { useUnreadMessagesCount } from "@/hooks/use-unread-messages-count"
import {
  agencyHomePath,
  badgesPath,
  brandAnalyticsMissionsPath,
  brandContentPath,
  brandHomePath,
  brandSettingsProfilePath,
  brandShipmentsPath,
  candidatesPath,
  crewLeaderboardPath,
  crewMessagesPath,
  crewPath,
  crewSegmentsPath,
  dashboardPath,
  invitationsPath,
  masterclassesPath,
  missionsPath,
  rewardsPath,
} from "@/routes"
import type { NavItem } from "@/types"

import AppLogo from "./app-logo"

/**
 * BrandSidebar — sidebar for the brand/agency shell.
 *
 * NOTE: NavUser is intentionally NOT rendered here. Per the OKS-5 UX Spec
 * (CTPO-approved), the user menu lives in the header-right slot. See
 * `<HeaderNavUser>` and `<BrandShell>`.
 *
 * Nav item definitions live INSIDE the component body so every title can
 * flow through `useTranslation()` — keeps EN strings out of the component file.
 *
 * OKS-7: the "Dashboard" nav item + logo home link route to the per-role
 * home (brandHomePath / agencyHomePath) rather than the legacy /dashboard
 * dispatcher. Falls back to dashboardPath() for unknown/missing role so
 * the backend dispatcher can still sort it out (fail-safe, not fail-loud).
 */
export function BrandSidebar() {
  const { t } = useTranslation()
  const { currentUser, currentAccount, actingAs } = useCurrentContext()
  const unreadMessagesCount = useUnreadMessagesCount()
  const pendingShipmentsCount = usePendingShipmentsCount()

  // OKS-346 — When an OKS Admin is operating inside the "act as brand"
  // context (`actingAs != null` on an `admin`-role request), they get the
  // BrandShell + BrandSidebar so every brand-scoped surface is navigable
  // (PRD R2 — parity with a Brand Admin). Treat them as a brand admin for
  // every nav-gate decision below. `currentUser.role` remains "admin"
  // (truthful; banner makes scope unambiguous per UX Spec OQ3).
  const actsAsBrandAdmin = currentUser?.role === "admin" && actingAs != null

  const homeHref =
    currentUser?.role === "agency"
      ? agencyHomePath()
      : currentUser?.role === "brand" || actsAsBrandAdmin
        ? // OKS-346 — admin-in-act-as routes the logo + dashboard entry
          // directly at brandHomePath(). The `DashboardController#dashboard`
          // dispatcher already redirects /dashboard → /brand for an admin
          // with `Current.acting_as`, so the destination is identical; this
          // just shaves the redirect hop off the most-clicked entry.
          brandHomePath()
        : dashboardPath()

  // OKS-11: mirror `InvitationPolicy#create_any?` — a brand/agency user can
  // reach the pending-invites list when they have a tenant (brand_id /
  // agency_id). In BrandShell they almost always do (zero-tenancy users are
  // bounced to /pending before the shell renders), but we still defend by
  // checking `currentAccount.id`. No "member" role in v1, so any seated
  // brand/agency user is effectively an admin for invite purposes.
  //
  // OKS-346 — admin-in-act-as sees the entry. `currentAccount.id` is set
  // to the admin's own user id by `ActsAsBrandResolver`, so the seated-
  // tenancy guard remains valid under act-as.
  const canSeeInvitations =
    (currentUser?.role === "brand" ||
      currentUser?.role === "agency" ||
      actsAsBrandAdmin) &&
    currentAccount.id !== null

  // OKS-672 — the top-level `Team` sidebar entry was removed; brand team
  // management now lives under Brand Settings (`/brand/settings/team`,
  // reachable via the Settings sub-nav "Team" tab). The sidebar carries
  // only crew-piloting surfaces. `canSeeBrandSettings` (below) gates the
  // Settings entry through which Team is now reached.

  // OKS-17/18 staging walkthrough gap: `/candidates` had no chrome
  // entry. `Recruiting` slots between Team and Invitations for
  // brand-role users only — agency-role users see the same affordance
  // via `AgencySidebar` (F1 split). `CandidatePolicy` already gates
  // `index?` server-side; the FE link gate just hides it from any
  // sidebar view that's not a brand user.
  //
  // Locked QA testid: `brand-sidebar-nav-recruiting`.
  //
  // OKS-346 — admin-in-act-as sees the entry (PRD R2 parity).
  const canSeeRecruiting = currentUser?.role === "brand" || actsAsBrandAdmin

  // OKS-25 — `Crew` entry between `Recruiting` and `Settings` per UX
  // Spec §Sidebar Nav. Brand-role only (agency-role mirror lives in
  // `AgencySidebar`). `CrewMembershipPolicy#index?` mirrors
  // `CandidatePolicy#permitted_role?` server-side; creator-role
  // explicitly denied. Title is the proper noun "Crew" — UX Spec
  // explicitly notes this is not translated (i18n key `nav.crew`
  // resolves to the same string in EN + FR).
  //
  // Locked testid (CONTRACT FROZEN by team-lead 2026-05-03): single
  // `sidebar-nav-crew`. Both `BrandSidebar` and `AgencySidebar` render
  // the same testid string — DOM uniqueness comes from whichever
  // shell is mounted for the active role.
  //
  // OKS-346 — admin-in-act-as sees the full Crew section (PRD R2 parity).
  const canSeeCrew = currentUser?.role === "brand" || actsAsBrandAdmin

  // OKS-21 — Brand Settings shell entry. Per memory
  // `sidebar_nav_for_new_routes`, every new top-level route gets a
  // sidebar entry in the same PR. v1 sub-pages = Invites only;
  // entry routes directly to `/brand/settings/invites`. Future
  // sub-pages add to `<BrandSettingsNav>` without sidebar churn.
  // Brand-role users see this; agency-role gets the equivalent
  // `agency-sidebar-nav-settings` mirror in `agency-sidebar.tsx`
  // (BE policy permits agency_member_of_brand to write).
  //
  // Locked QA testid: `brand-sidebar-nav-settings`.
  //
  // OKS-346 — admin-in-act-as sees Settings (PRD R2 parity — this entry
  // is the headline navigability gap fixed by this ticket).
  const canSeeBrandSettings = currentUser?.role === "brand" || actsAsBrandAdmin

  // OKS-346 — Messages carve-out under act-as.
  //
  // `ConversationPolicy#index?` denies admin per OKS-312 §6 PRD privacy
  // carve-out (V1: admin-in-act-as cannot read brand-creator
  // conversations — the messaging surface is a writes-blocked + reads-
  // out-of-scope carve-out from the otherwise-full act-as parity).
  // Concretely: `GET /crew/messages` 404s for admin-in-act-as. Rendering
  // the entry would violate PRD A2 ("every link resolves") — the entry
  // would be visible but every click 404s.
  //
  // Resolution (PR-review override available): HIDE the Messages entry
  // while admin is acting-as. Pure-brand admins still see it (their
  // entry resolves fine). PRD A2 takes precedence over R2 byte-for-byte
  // parity — a clickable-but-404 entry is worse than a missing entry,
  // and the banner already signals act-as-scope so its absence won't
  // confuse the admin about what they're looking at.
  //
  // Detected by LQE in `spec/requests/brand_isolation/
  // act_as_sidebar_isolation_spec.rb` (locked 404 reality, defensive
  // canary — turns red if either the policy widens OR this carve-out
  // moves). Flagged for CTPO PR-review per TRD Open Questions.
  const canSeeMessages = currentUser?.role === "brand"

  const mainNavItems: NavItem[] = [
    {
      title: t("nav.dashboard"),
      href: homeHref,
      icon: LayoutGrid,
    },
    ...(canSeeRecruiting
      ? [
          {
            title: t("brand_nav.recruiting"),
            href: candidatesPath(),
            icon: UserSearch,
            "data-testid": "brand-sidebar-nav-recruiting",
          },
        ]
      : []),
    ...(canSeeCrew
      ? [
          {
            title: t("nav.crew"),
            href: crewPath(),
            icon: UsersRound,
            "data-testid": "sidebar-nav-crew",
          },
          // OKS-28 — Segments management nav entry. Sits directly
          // below "Crew" so the two surfaces read as a section.
          // Brand-side and agency-side mirror each other (both use
          // `sidebar-nav-crew-segments` testid).
          //
          // Brand-role only — `CrewSegmentPolicy#index?` mirrors the
          // roster gate (admin OR brand member OR agency assigned;
          // creators denied). Server is the floor.
          //
          // Locked QA testid: `sidebar-nav-crew-segments` (shared
          // across both sidebars; DOM uniqueness comes from whichever
          // shell is mounted for the active role).
          {
            title: t("crew_segments.nav.segments"),
            href: crewSegmentsPath(),
            icon: Bookmark,
            "data-testid": "sidebar-nav-crew-segments",
          },
          // OKS-29 — Missions nav entry. Sits below "Segments" so the
          // Crew section reads roster → segments → missions (the cycle-3
          // mental model: who's in the crew, how to slice them, what to
          // brief them on).
          //
          // Brand-role only — `MissionPolicy#index?` mirrors the roster
          // gate (admin OR brand member OR agency assigned; creators
          // denied). Server is the floor.
          //
          // Locked QA testid (per ticket): `brand-sidebar-nav-missions`
          // on the brand shell; `agency-sidebar-nav-missions` on the
          // agency shell. Distinct testids (vs OKS-28's shared
          // `sidebar-nav-crew-segments`) — keeps the per-shell DOM
          // queryable independently in Vitest.
          {
            title: t("nav.missions"),
            href: missionsPath(),
            icon: Megaphone,
            "data-testid": "brand-sidebar-nav-missions",
          },
          // OKS-54 — Masterclass library nav entry. Sits below Missions
          // so the Crew section reads roster → segments → missions →
          // training (briefing + content sit next to formation).
          //
          // `MasterclassPolicy#index?` mirrors the missions gate (admin
          // OR brand member OR agency assigned; creators denied).
          //
          // Locked QA testid: `sidebar-nav-masterclasses` (shared
          // across BrandSidebar + AgencySidebar mirror; DOM uniqueness
          // via active shell).
          {
            title: t("nav.training"),
            href: masterclassesPath(),
            icon: GraduationCap,
            "data-testid": "sidebar-nav-masterclasses",
          },
          // OKS-39 — Content gallery nav entry. Sits between Missions
          // and Messages so the Crew section reads roster → segments →
          // missions → content → messages (operator mental model:
          // "what got posted, by whom, when").
          //
          // Brand-role only on this sidebar; agency-role users see the
          // mirror entry on `AgencySidebar` if/when added (V2 candidate;
          // V1 agency surface inherits via brand impersonation).
          // `ContentGalleryPolicy#index?` server-side: admin OR
          // brand_member OR agency_member_of_brand. Creators denied
          // (PRD OQ-2 — no creator gallery in V1).
          //
          // Locked QA testid (shared across BrandSidebar + future
          // AgencySidebar mirror; DOM uniqueness via active shell):
          // `sidebar-nav-content-gallery`.
          // OKS-984 — Shipments queue. Sits between Missions and Content
          // per UX-OQ-1: it is an operating surface tied to missions, not a
          // report. Count chip reuses the Messages-unread pattern and shows
          // READY-TO-SHIP only (PRD OQ-5) — a badge including rows the
          // operator cannot act on never reaches zero and gets ignored.
          //
          // Mirrored in BOTH shells: an agency operator assigned to the brand
          // does this job as much as a brand admin does. Per-shell testid
          // (`brand-` / `agency-` prefix) follows the Missions convention.
          {
            title: t("nav.shipments"),
            href: brandShipmentsPath(),
            icon: Truck,
            "data-testid": "brand-sidebar-nav-shipments",
            unreadCount: pendingShipmentsCount,
            unreadAriaLabel: t("shipments.badge_aria", {
              count: pendingShipmentsCount,
            }),
          },
          {
            title: t("nav.content_gallery"),
            href: brandContentPath(),
            icon: Image,
            "data-testid": "sidebar-nav-content-gallery",
          },
          // OKS-63 — Brand-side Mission Analytics nav entry. Sits
          // between Content (gallery) and Leaderboard so the Crew
          // section reads roster → segments → missions → content →
          // analytics → recognition (UX Spec §1 / TRD K14). Sets up
          // the V2 expansion for OKS-64/65 (creators / crew analytics
          // tabs) inside this same surface.
          //
          // Brand-role + agency-of-brand only on this sidebar (server
          // gate is `MissionAnalyticsPolicy#index?` — admin OR
          // brand_member OR agency_member_of_brand; creator denied,
          // no creator analytics surface).
          //
          // Locked testid: `brand-sidebar-nav-analytics`. Read-only
          // surface; no act-as widening (matches the OKS-313
          // CARVE_OUTS posture for this policy).
          {
            title: t("nav.analytics"),
            href: brandAnalyticsMissionsPath(),
            icon: BarChart3,
            "data-testid": "brand-sidebar-nav-analytics",
          },
          {
            // OKS-51 — Leaderboard nav entry. Sits inside the Crew
            // section, sibling to roster + segments + missions +
            // content + messages. UX Spec §2.2 lock: brand admins +
            // agency-of-brand reach the leaderboard from the Crew
            // section (NOT under Analytics — recognition tool tied to
            // the crew, not a report).
            //
            // `BrandLeaderboardPolicy#show?` server-side: admin +
            // brand_member + agency_member_of_brand. Creators denied
            // (they have their own `/leaderboard` surface in the
            // creator app shell).
            //
            // Locked testid: `sidebar-nav-leaderboard`.
            title: t("nav.leaderboard"),
            href: crewLeaderboardPath(),
            icon: Trophy,
            "data-testid": "sidebar-nav-leaderboard",
          },
          // OKS-59 — Rewards catalogue nav entry. Sits between
          // Leaderboard and Messages (UX Spec §1 / PRD OQ-5): rewards
          // are a crew-engagement surface, not a settings/admin one.
          //
          // `RewardPolicy#index?` permits brand_member + agency-of-brand
          // + admin (server-side floor); creators are denied. The
          // sidebar gate uses the same `canSeeCrew` predicate as the
          // rest of the Crew section.
          //
          // Locked QA testid: `sidebar-nav-rewards` (shared across
          // BrandSidebar + AgencySidebar; DOM uniqueness via the
          // active shell).
          {
            title: t("nav.rewards"),
            href: rewardsPath(),
            icon: Gift,
            "data-testid": "sidebar-nav-rewards",
          },
          // OKS-572 — Badges catalogue nav entry. Closes the recognition
          // trio Leaderboard → Rewards → Badges, immediately after
          // Rewards and before Messages. The catalogue page, `/badges`
          // route, and `BadgePolicy#index?` all shipped in OKS-538; this
          // is purely the missing discoverability affordance.
          //
          // `BadgePolicy#index?` (`show_or_read?`) permits admin +
          // brand_member + agency_member_of_brand; creators denied (they
          // have their own `/profile/badges` surface). The sidebar gate
          // uses the same `canSeeCrew` predicate as the rest of the Crew
          // section — identical visibility to the Rewards sibling above.
          //
          // Per-shell distinct testid: `brand-sidebar-nav-badges` (vs
          // `agency-sidebar-nav-badges` on the agency shell) — follows
          // the Missions per-shell-testid convention (UX Spec §2 lock),
          // NOT the shared `sidebar-nav-*` convention used by Rewards /
          // Masterclasses.
          {
            title: t("nav.badges"),
            href: badgesPath(),
            icon: Award,
            "data-testid": "brand-sidebar-nav-badges",
          },
          // OKS-37 — Messaging nav entry. Sits below Leaderboard so the
          // Crew section closes with the conversation surface (roster →
          // segments → missions → … → messages). Brand AND agency
          // sidebars mirror each other on this entry —
          // `ConversationPolicy#index?` permits both brand_member and
          // currently-assigned agency. Single shared testid
          // (`sidebar-nav-messages`) for DOM uniqueness via active
          // shell only. Unread chip surfaces the `unread_messages_count`
          // shared prop (cap 99 server-side; UX Spec §4 — sidebar
          // Messages entry shows count chip when unread > 0).
          //
          // OKS-346 — Carved out for admin-in-act-as via `canSeeMessages`
          // (see gate declaration above for full rationale). The brand-
          // role branch still gets it unconditionally.
          ...(canSeeMessages
            ? [
                {
                  title: t("messaging.brand.nav"),
                  href: crewMessagesPath(),
                  icon: MessageCircle,
                  "data-testid": "sidebar-nav-messages",
                  unreadCount: unreadMessagesCount,
                  unreadAriaLabel: t("messaging.brand.list.unread_aria", {
                    count: unreadMessagesCount,
                  }),
                },
              ]
            : []),
        ]
      : []),
    ...(canSeeInvitations
      ? [
          {
            title: t("nav.invitations"),
            href: invitationsPath(),
            icon: MailPlus,
          },
        ]
      : []),
    ...(canSeeBrandSettings
      ? [
          {
            title: t("brand_nav.settings"),
            href: brandSettingsProfilePath(),
            icon: Settings,
            "data-testid": "brand-sidebar-nav-settings",
          },
        ]
      : []),
  ]

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link href={homeHref} prefetch>
                <AppLogo />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <NavMain items={mainNavItems} />
      </SidebarContent>
    </Sidebar>
  )
}
